/**
 * Generic keyboard shortcuts hook.
 * Can be used by any product to bind shortcuts to handlers with optional scope management.
 */

import { useHandler } from "@redotech/react-util/hook";
import { useEffect, useMemo } from "react";
import {
  getEventShortcutKey,
  getShortcutKey,
  shouldHandleKeyboardEvent,
  type KeyboardShortcut,
} from "./keyboard-shortcut";

export interface ShortcutBinding {
  shortcut: KeyboardShortcut;
  handler: () => void;
}

export interface UseKeyboardShortcutsOptions<TScope extends string = string> {
  /** Array of shortcut-to-handler bindings */
  bindings: readonly ShortcutBinding[];
  /** The scope ID for these shortcuts (used for shortcut key matching) */
  scope: TScope;
  /** Whether shortcuts are enabled (default: true) */
  enabled?: boolean;
  /**
   * If provided, enables Escape handling:
   * - Calls popScope (if provided) then onEscape when Escape is pressed
   */
  onEscape?: () => void;
  /**
   * Optional scope management callbacks.
   * If provided along with onEscape, pushScope is called on mount
   * and popScope is called on unmount.
   * Note: pushScope takes no parameters - capture the scope in a closure.
   */
  scopeManagement?: { pushScope: () => void; popScope: () => void };
}

/**
 * Generic hook for binding keyboard shortcuts to handlers.
 * Works with any scope type and optional scope stack management.
 *
 * @example
 * // Simple usage without scope management
 * useKeyboardShortcuts({
 *   bindings: [
 *     { shortcut: { code: "KeyJ", keyLabel: "J" }, handler: navigateDown },
 *     { shortcut: { code: "KeyK", keyLabel: "K" }, handler: navigateUp },
 *   ],
 *   scope: "table",
 * });
 *
 * @example
 * // With scope management (for submenus)
 * useKeyboardShortcuts({
 *   bindings: priorityOptions.map(opt => ({
 *     shortcut: opt.shortcut,
 *     handler: () => onSelect(opt.value),
 *   })),
 *   scope: "priority",
 *   onEscape: onClose,
 *   scopeManagement: { pushScope, popScope },
 * });
 */
export function useKeyboardShortcuts<TScope extends string = string>({
  bindings,
  scope,
  enabled = true,
  onEscape,
  scopeManagement,
}: UseKeyboardShortcutsOptions<TScope>): void {
  const shortcutMap = useMemo(() => {
    const map = new Map<string, () => void>();
    for (const { shortcut, handler } of bindings) {
      map.set(getShortcutKey(shortcut, scope), handler);
    }
    return map;
  }, [bindings, scope]);

  const handleKeyDown = useHandler((event: KeyboardEvent) => {
    if (!shouldHandleKeyboardEvent(event)) {
      return;
    }

    if (onEscape && event.key === "Escape") {
      event.preventDefault();
      scopeManagement?.popScope();
      onEscape();
      return;
    }

    const eventKey = getEventShortcutKey(event, scope);
    const handler = shortcutMap.get(eventKey);

    if (handler) {
      event.preventDefault();
      handler();
    }
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Push scope on mount if scope management is enabled and we have onEscape
    if (onEscape && scopeManagement) {
      scopeManagement.pushScope();
    }

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      // Pop scope on unmount if we pushed it
      if (onEscape && scopeManagement) {
        scopeManagement.popScope();
      }
    };
  }, [enabled, onEscape, scope, scopeManagement, handleKeyDown]);
}
